import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const EditClientModal = ({ client, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    business_name: client.business_name || client.name || '',
    email: client.email || '',
    phone: client.phone || client.business_phone || '',
    industry: client.industry || '',
    revenue: client.revenue || client.monthly_revenue || '',
    dob: client.dob || '',
    ssn: client.ssn || '',
    status: client.status || 'Active',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.put(`${API_BASE}/clients/${client.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cascaded = res?.data?.cascaded_deals || 0;
      toast({
        title: 'Client updated',
        description: cascaded > 0
          ? `Also updated ${cascaded} linked funded deal${cascaded === 1 ? '' : 's'}.`
          : 'Changes saved.'
      });
      if (onSuccess) onSuccess(); else onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.error || 'Failed to update client',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, k, type = 'text', required = false, placeholder = '' }) => (
    <div>
      <label className="block text-sm font-medium mb-2">{label}{required && ' *'}</label>
      <Input
        required={required}
        type={type}
        value={formData[k]}
        onChange={(e) => setFormData({ ...formData, [k]: e.target.value })}
        placeholder={placeholder}
        data-testid={`edit-client-${k}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Edit Client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Contact person's name — saves cascade to funded deals as client_contact_name */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" k="first_name" required />
            <Field label="Last Name" k="last_name" required />
          </div>

          {/* Business name change cascades to deal.business on every linked deal */}
          <div>
            <label className="block text-sm font-medium mb-2">Business Name *</label>
            <Input
              required
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              data-testid="edit-client-business_name"
            />
            <p className="text-xs text-gray-500 mt-1">Changes here update the business name on every linked Funded Deal.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" k="email" type="email" required />
            <Field label="Phone" k="phone" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Industry" k="industry" required />
            <Field label="Monthly Revenue" k="revenue" placeholder="$50,000" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Date of Birth" k="dob" type="date" />
            <Field label="SSN" k="ssn" placeholder="123-45-6789" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Status *</label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border rounded p-2"
              data-testid="edit-client-status"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-orange-500 hover:bg-orange-600" data-testid="edit-client-save">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClientModal;
